import * as React from 'react';
import styled from '@emotion/styled';
import { hasSoldOut } from '../../helpers/priceHelpers';
import { __ } from '../../helpers/TranslationService';
import { ICabinInfo, IImage } from '../../interfaces';
import { CabinDetailsDialog } from '../CabinDetailsDialog/CabinDetailsDialog';
import { CabinInfo } from './CabinInfo';
import { InfoIconNew } from '../Icons/InfoIconNew';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { getCabinAllInPriceByRates } from '../PriceString';

interface IProps {
    kind: string;
    price: number;
    discount: number;
    currency: string;
    cabins: ICabinInfo[];
    minPriceCabinNid: number;
    url: string;
    sailNid: number;
    isLastItem: boolean;
    image: IImage;
    isPriceShown: boolean;
    learnMoreMode?: boolean;
    isCabinSelectShown: boolean;
    cheapestCabinId?: number;
    onCabinSelection?: (event: any) => void;
    isSelectedCabin?: boolean;
}

const CabinWrapper = styled.div<{ isSelectedCabin: boolean; disabled?: boolean }>`
    display: flex;
    background-color: #fff;
    border-radius: 6px;
    margin: 0;
    min-width: 320px;
    width: 100%;
    padding: 16px;
    align-items: center;
    box-shadow: 0px 2px 6px 0px rgba(0, 0, 0, 0.12);

    .disabled {
        color: ${({ theme }) => theme.colors.whiteSmoke[100]};
    }

    .cabinLinkArrowIcon {
        margin-left: 3px;
        fill: ${({ theme }) => theme.colors.whiteSmoke[100]};
    }

    .cabinInfoBar {
        width: 100%;
        > img {
            width: 80px;
        }
        > h4 {
            margin: 0;
            font-size: 16px;
        }
        &__learnMore {
            h4 {
                margin: 10px;
            }
            .link {
                margin-bottom: 0;
            }
            .cabinInfoData {
                h4 {
                    margin: 10px;
                }
            }
        }
    }
`;
const CabinOverlayLinkStyled = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.link.primary};
    margin-left: auto;
    padding-left: 16px;
    height: 100%;
`;

export const Cabin = ({
    cabins,
    currency,
    price,
    discount,
    kind,
    sailNid,
    minPriceCabinNid,
    isLastItem,
    image,
    isPriceShown,
    learnMoreMode,
    cheapestCabinId,
    isSelectedCabin,
    url,
    isCabinSelectShown,
    onCabinSelection,
}: IProps) => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { cruisePage, pageData } = useTypedSelector((state) => state);
    const { discountThreshold } = pageData;
    const getModal = () => {
        const cabin = isPriceShown
            ? cabins.find((item) => item.cabinType.nid === minPriceCabinNid)
            : cabins[0];

        return (
            <CabinDetailsDialog
                cruisePage={cruisePage}
                discountThreshold={pageData.discountThreshold}
                cabins={cabins}
                cabin={cabin}
                urlBase={`${url}/${sailNid}`}
                isPriceShown={isPriceShown}
                isCabinSelectShown={isCabinSelectShown}
                onClose={hideModal}
            />
        );
    };

    const hideModal = () => setIsModalOpen(false);

    const showCabinDetailOverlay = () => setIsModalOpen(true);

    const { sails, selectedSailIndex, soldOutBooking, includesFlight, rates } = cruisePage;

    const selectedRates = rates?.filter(
        (rate) => rate.sail_nid === cruisePage?.sails[cruisePage.selectedSailIndex]?.nid,
    );

    const allInPrice = getCabinAllInPriceByRates(selectedRates, kind);

    const label = __(isPriceShown ? 'Request cruise now' : 'Cabin details', 'dreamlines');

    const isDisabled =
        sailNid > 0 && hasSoldOut(sails[selectedSailIndex].departure, soldOutBooking, price);

    return (
        <CabinWrapper isSelectedCabin={isSelectedCabin}>
            <CabinInfo
                currency={currency}
                price={price}
                allInPrice={allInPrice}
                discount={discount}
                discountThreshold={discountThreshold}
                kind={kind}
                isLastItem={isLastItem}
                image={image}
                cabins={cabins}
                sailNid={sailNid}
                labelCaption={label}
                isPriceShown={isPriceShown}
                learnMoreMode={learnMoreMode}
                includesFlight={includesFlight}
                onClick={!isDisabled ? showCabinDetailOverlay : null}
                departure={sailNid > 0 ? sails[selectedSailIndex].departure : undefined}
                soldOutBooking={soldOutBooking}
                cabinId={minPriceCabinNid}
                cheapestCabinId={cheapestCabinId}
                onCabinSelection={onCabinSelection}
                isSelectedCabin={isSelectedCabin}
                operator={cruisePage.company.bookingServiceCode}
            />
            {(isPriceShown || learnMoreMode) && (
                <CabinOverlayLinkStyled onClick={showCabinDetailOverlay}>
                    <InfoIconNew width={20} height={20} />
                </CabinOverlayLinkStyled>
            )}
            {isModalOpen && getModal()}
        </CabinWrapper>
    );
};
